import { useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
	actionSetMessage,
	actionResetMessages,
} from 'components/messages/actions.js';
import { DefaultColumnFilter } from 'components/utils/tableFilters.js';
import { CheckAuth } from 'pages/auth/CheckAuth';
import Header from 'components/header.js';
import Footer from 'components/footer.js';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import OPWBreadcrumb from 'components/breadcrumb.js';
import Loading from 'components/loading.js';
import RptBtns from 'components/rptBtns.js';

import { ClientSessionSelect } from 'components/clientSessionSelect/clientSessionSelect';
import { actionFetchSessionAvg, EidWeightSummary } from './actions';
import { TableControl, TableStyles } from 'components/tableControl.js';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import { kgToLbs } from 'components/utils/convert-units.js';
import { format } from 'date-fns-tz';

const TITLE = 'Session data';
const showRecCount = 50; // default records to display
const BC = OPWBreadcrumb;

const WeightPeriods = {
	1: 'Last day',
	5: 'Last 5 days',
	10: 'Last 10 days',
	all: 'Session average',
};
const AdgPeriods = {
	MA_14: 'Last 14 days',
	MA_30: 'Last 30 days',
	MA_90: 'Last 90 days',
	MA_180: 'Last 180 days',
	MA_all: 'All Data',
};

//:TODO: add totals on footer https://react-table.tanstack.com/docs/examples/footers

// this is specifically for this table and overwrites TableStyles.
const Session2TableStyles = styled.div`
	th#visId input {
		max-width: 4rem;
	}
	th#eid input {
		max-width: 8rem;
	}
	tr {
		td {
			padding-left: 0.2rem;
			padding-right: 0.2rem;
		}
	}
`;

const ParamPanel = (props: {
	loading: boolean;
	handleDownload: () => void;
}) => {
	const { loading, handleDownload } = props;

	return (
		<Card className={'no-print'} body>
			<Card.Title>{TITLE}</Card.Title>
			<Card.Body>
				<Row>
					<Col sm={4}>
						<ClientSessionSelect
							hideSessionSelect={false}
							includeYardWeights={false}
						/>
					</Col>
					<Col sm={4}>
						<div className={'w-50 mx-auto'}>
							<Loading loading={loading} />
						</div>
					</Col>
					<Col sm={4}>
						<div className={'text-right report-buttons'}>
							<RptBtns
								enablePrint={true}
								enableHelp={true}
								enableDownload={true}
								handleDownload={() => handleDownload()}
								downloading={false}
							/>
						</div>
						<p className="dt-leftMargin note">
							Click column head ▽ to sort data.
						</p>
					</Col>
				</Row>
			</Card.Body>
		</Card>
	);
};

// pagination
//https://medium.com/better-programming/an-introduction-to-react-table-6ebd34d8059e
export const SessionData2 = () => {
	const [data, setData] = useState<EidWeightSummary[]>([]); //	local data for graph or report
	const [loading, setLoading] = useState(false);
	const [weight_period, set_weight_period] = useState('5');
	const [adg_period, set_adg_period] = useState('MA_30');
	const session = useAppSelector((state) => state.session);
	const dispatch = useAppDispatch();

	const pounds = session?.displayUnits === 'i';
	const is_sheep = session?.species?.toUpperCase() === 'SHEEP';
	const adg_decimals = is_sheep ? 2 : 1;
	const weight_decimals = is_sheep ? 1 : 0;

	const format_weight = useCallback(
		(w: number | null, decimals: number): number | null => {
			if (!w) return w;
			if (pounds) return +kgToLbs(w).toFixed(decimals);
			return +w.toFixed(decimals);
		},
		[pounds]
	);

	const handleDownload = useCallback(async () => {
		if (!session) return;
		const headers =
			'eid,visId,weight' +
			(session.adjustFactor ? `,"${session.adjustName ?? 'adjusted'}"` : '') +
			',n,latest,days,adg\n';
		const csv = data
			.map((row) =>
				[
					row.eid,
					row.visId,
					format_weight(row.weight[weight_period], weight_decimals),
					...(session.adjustFactor
						? [
								row.weight[weight_period] &&
									format_weight(
										session.adjustFactor * row.weight[weight_period],
										weight_decimals
									),
							]
						: []),
					row.n,
					row.latest,
					row.days,
					row.adg,
				].join(',')
			)
			.join('\n');
		const blob = new Blob([headers + csv], { type: 'text/csv;charset=utf-8' });
		await saveAs(blob, session.SessionName + '.csv');
	}, [data, session, weight_period, weight_decimals, format_weight]);

	useEffect(() => {
		document.title = TITLE;
	}, []);

	const showMessage = useCallback(
		(data: string) => dispatch(actionSetMessage(data)),
		[dispatch]
	);

	useEffect(() => {
		async function fetchData() {
			dispatch(actionResetMessages());
			const resp = await actionFetchSessionAvg(
				session!.SessionID,
				adg_period,
				showMessage,
				setLoading
			);
			setData(resp);
		}
		if (session) {
			setLoading(true);
			fetchData();
		} else {
			setData([]);
		}
	}, [
		session,
		format_weight,
		adg_period,
		weight_decimals,
		adg_decimals,
		showMessage,
		dispatch,
	]);

	return (
		<Template>
			<ParamPanel loading={loading} handleDownload={handleDownload} />
			<h2 className={'only-print'}>Optiweigh - Session data</h2>
			<SessionDataTable
				data={data}
				pounds={pounds}
				adg_decimals={adg_decimals}
				wt_decimals={weight_decimals}
				adg_period={adg_period}
				wt_period={weight_period}
				handleWeightPeriod={(p: string) => set_weight_period(p)}
				handleAdgPeriod={(p: string) => set_adg_period(p)}
				adjust_name={session?.adjustName}
				adjust_factor={session?.adjustFactor}
			/>
		</Template>
	);
};

const Template = (props: { children: ReactNode }) => (
	<>
		<Header>
			<BC trail='[["Home", "/"], ["reports", "/"], ["session data", ""]]' />
		</Header>
		<Container fluid className="body">
			<CheckAuth groupName={'user'} />
			{props.children}
		</Container>
		<Container fluid>
			<Row>
				<Col sm="12">
					<Footer />
				</Col>
			</Row>
		</Container>
	</>
);

const PeriodSelect = (props: {
	values: { [key: string]: string };
	current?: string;
	handleChange: (v: string) => void;
}) => {
	const { values, current, handleChange } = props;

	return (
		<Form.Control
			as="select"
			style={{
				appearance: 'listbox',
				marginLeft: '1rem',
				display: 'inline',
				width: '12rem',
			}}
			value={current ?? ''}
			onChange={(e) => handleChange(e.target.value)}
		>
			{Object.keys(values).map((key, i) => {
				return (
					<option key={key} value={key}>
						{values[key]}
					</option>
				);
			})}
		</Form.Control>
	);
};

function SessionDataTable(props: {
	data: EidWeightSummary[];
	pounds: boolean;
	adg_decimals: number;
	wt_decimals: number;
	adg_period: string;
	wt_period: string;
	adjust_name?: string | null;
	adjust_factor?: number | null;
	handleWeightPeriod: (p: string) => void;
	handleAdgPeriod: (p: string) => void;
}) {
	const {
		data,
		pounds,
		adg_decimals,
		wt_decimals,
		adg_period,
		wt_period,
		adjust_name,
		adjust_factor,
		handleWeightPeriod,
		handleAdgPeriod,
	} = props;

	const adjusted = useCallback(
		(wt?: number | null) => {
			return wt && wt * (adjust_factor ?? 1);
		},
		[adjust_factor]
	);

	const average_fn = (
		info: any,
		selector: (row: any) => number,
		value?: (row: any) => number
	) => {
		const avgObj = info.rows.reduce(
			(dataObj: { sum: number; count: number }, row: any) => {
				if (selector(row.original)) {
					dataObj.sum += (value ?? selector)(row.original);
					dataObj.count++;
				}
				return dataObj;
			},
			{ sum: 0, count: 0 }
		);
		return avgObj.count > 0 ? avgObj.sum / avgObj.count : null;
	};

	const columns = useMemo(
		() => [
			{
				Header: 'Animal',
				Footer: '',
				columns: [
					{
						Header: 'RFID',
						Footer: 'Average',
						accessor: 'eid',
						sortType: 'basic',
						style: { textAlign: 'center' },
						Filter: DefaultColumnFilter,
					},
					{
						Header: 'Visual ID',
						Footer: '',
						accessor: 'visId',
						sortType: 'basic',
						style: { textAlign: 'center' },
						Filter: DefaultColumnFilter,
					},
				],
			},
			{
				id: 'wt_period',
				Header: (
					<>
						Weight
						<PeriodSelect
							values={WeightPeriods}
							handleChange={handleWeightPeriod}
						/>
					</>
				),
				Footer: '',
				columns: [
					{
						Header: `Average [${pounds ? 'lb' : 'kg'}]`,
						Footer: (info: any) => {
							const avg = useMemo(
								() => average_fn(info, (row: any) => row.weight[wt_period]),
								[info]
							);
							return avg?.toFixed(wt_decimals);
						},
						accessor: (row: any) => {
							return row.weight[wt_period]?.toFixed(wt_decimals);
						},
						style: { textAlign: 'right' },
					},
					...(adjust_factor
						? [
								{
									Header: `Adjusted (${adjust_name ?? ''}) [${pounds ? 'lb' : 'kg'}]`,
									Footer: (info: any) => {
										const avg = useMemo(
											() =>
												average_fn(info, (row: any) => row.weight[wt_period]),
											[info]
										);
										return avg && adjusted(avg)?.toFixed(wt_decimals);
									},
									accessor: (row: any) => {
										return adjusted(row.weight[wt_period])?.toFixed(
											wt_decimals
										);
									},
									style: { textAlign: 'right' },
								},
							]
						: []),
				],
			},
			{
				id: 'adg_period',
				Header: (
					<>
						Average Daily Gain
						<PeriodSelect
							values={AdgPeriods}
							current={adg_period}
							handleChange={handleAdgPeriod}
						/>
					</>
				),
				columns: [
					{
						Header: 'Count of Weights',
						Footer: (info: any) => {
							const avg = useMemo(
								() => average_fn(info, (row: any) => row.n),
								[info]
							);
							return avg?.toFixed(0);
						},
						//https://github.com/tannerlinsley/react-table/blob/master/examples/footers/src/App.js
						//https://react-table.tanstack.com/docs/examples/footers
						accessor: 'n',
						sortType: 'basic',
						style: { textAlign: 'right' },
					},
					{
						Header: 'Last Date',
						accessor: (row: any) => {
							if (row.latest) return format(new Date(row.latest), 'yyyy-MM-dd');
							return '';
						},
						sortType: 'basic',
						style: { textAlign: 'right' },
					},
					{
						Header: `ADG [${pounds ? 'lb' : 'kg'}/day]`,
						Footer: (info: any) => {
							const avg = useMemo(
								() => average_fn(info, (row: any) => row.adg),
								[info]
							);
							return avg?.toFixed(adg_decimals);
						},
						accessor: (row: any) => row.adg?.toFixed(adg_decimals),
						style: { textAlign: 'right' },
					},
					{
						Header: 'Gain duration [days]',
						accessor: (row) => row.days || null,

						style: { textAlign: 'right' },
						Footer: (info) => {
							const avg = useMemo(
								() =>
									average_fn(
										info,
										(row: any) => (row.adg != null ? 1 : 0),
										(row: any) => row.days
									),
								[info]
							);
							return avg?.toFixed(0);
						},
					},
				],
			},
		],
		[
			pounds,
			adg_decimals,
			wt_decimals,
			adg_period,
			wt_period,
			adjust_name,
			adjusted,
			handleWeightPeriod,
			handleAdgPeriod,
		]
	);
	return (
		<TableStyles className="sessionDataTable">
			<Session2TableStyles>
				<TableControl
					columns={columns}
					data={data}
					showRecCount={showRecCount}
					handleCellUpdate={() => {}}
				/>
			</Session2TableStyles>
		</TableStyles>
	);
}
